import { useState } from "react";
import { Building2, CalendarDays, CheckCircle2, KeyRound, MapPin } from "lucide-react";
import { get } from "@/lib/api";
import { STATUS_HEX } from "@/lib/mapColors";
import { useAsync } from "@/lib/queries";
import { formatDate, formatTime, progressOf } from "@/lib/constants";
import { TeamMap } from "@/components/map/TeamMap";
import { PinPopup } from "@/components/map/PinPopup";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { EmptyState, ErrorState, LoadingRows } from "@/components/States";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "all";

const Stat = ({ label, value, icon: Icon, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="font-data text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <Icon className="h-4 w-4 text-blue-600" />
    </div>
    <p className="font-display mt-2 text-3xl font-extrabold text-slate-900" data-testid={testId}>{value}</p>
  </div>
);

const OverviewRow = ({ run }) => {
  const p = progressOf(run.run_addresses);
  return (
    <div data-testid={`overview-run-${run.id}`} className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-[80px_1.2fr_1fr_160px_120px] sm:items-center sm:gap-3">
      <span className="font-data text-sm font-semibold text-blue-800">{formatTime(run.start_time)}</span>
      <span className="min-w-0">
        <span className="block truncate font-semibold text-slate-900">{run.locksmith?.full_name || run.locksmith?.email || "Open job"}</span>
        <span className="block truncate text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid={`overview-run-company-${run.id}`}>{run.company_name}</span>
      </span>
      <span className="truncate text-sm text-slate-600">Officer: {run.officer?.full_name || "—"}</span>
      <ProgressBar done={p.done} total={p.total} compact />
      <span className="sm:justify-self-end"><StatusBadge status={run.status} /></span>
    </div>
  );
};

const TodayCard = ({ run }) => {
  const p = progressOf(run.run_addresses);
  return (
    <div data-testid={`overview-run-${run.id}`} className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="block font-semibold text-slate-900">{run.locksmith?.full_name || run.locksmith?.email || "Open job"}</span>
          <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid={`overview-run-company-${run.id}`}>{run.company_name}</span>
        </span>
        <StatusBadge status={run.status} />
      </div>
      <p className="text-sm text-slate-600"><span className="font-data font-semibold text-blue-800">{formatTime(run.start_time)}</span> · Officer: {run.officer?.full_name || "—"}</p>
      <ProgressBar done={p.done} total={p.total} compact />
    </div>
  );
};

const mapPoints = (runs) => runs.flatMap((run) => run.addresses.filter((a) => a.lat != null).map((a) => ({
  key: a.id, lat: a.lat, lng: a.lng, area: a.area, boundary: a.boundary, color: STATUS_HEX[a.status] || STATUS_HEX.scheduled, pulse: a.status === "in_progress",
  popup: <PinPopup run={run} address={a} readOnly />,
})));

const byDay = (runs) => {
  const days = new Map();
  runs.forEach((r) => days.set(r.run_date, [...(days.get(r.run_date) || []), r]));
  return [...days.entries()];
};

const CompanyFilter = ({ companies, value, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full bg-white sm:w-72" data-testid="overview-company-filter" aria-label="Filter by company"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL} data-testid="overview-company-option-all">All companies ({companies.length})</SelectItem>
      {companies.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`overview-company-option-${c.id}`}>{c.name}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const OwnerOverview = () => {
  const [company, setCompany] = useState(ALL);
  const { data, loading, error, reload } = useAsync(() => get("/platform/overview", company === ALL ? {} : { company_id: company }), [company]);
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <LoadingRows rows={3} />;
  const todays = data.runs.filter((r) => r.run_date === data.date);
  const upcoming = data.runs.filter((r) => r.run_date !== data.date);
  const tp = progressOf(todays.flatMap((r) => r.run_addresses));
  const points = mapPoints(data.map_runs);
  return (
    <div className={`space-y-8 transition-opacity ${loading ? "opacity-60" : ""}`} data-testid="owner-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CompanyFilter companies={data.companies} value={company} onChange={setCompany} />
        <p className="text-sm text-slate-500" data-testid="overview-readonly-note">Other changes require opening a company with View as manager.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Companies" value={company === ALL ? data.companies.length : 1} icon={Building2} testId="overview-stat-companies" />
        <Stat label="Runs today" value={todays.length} icon={CalendarDays} testId="overview-stat-runs-today" />
        <Stat label="Addresses today" value={tp.total} icon={MapPin} testId="overview-stat-addresses-today" />
        <Stat label="Completed today" value={`${tp.pct}%`} icon={CheckCircle2} testId="overview-stat-completed-today" />
        <Stat label="Active locksmiths" value={data.active_locksmiths} icon={KeyRound} testId="overview-stat-active-locksmiths" />
      </div>
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <h2 className="font-display text-lg font-bold text-slate-900">Today · {formatDate(data.date)}</h2>
          {todays.length ? todays.map((r) => <TodayCard key={r.id} run={r} />)
            : <EmptyState icon={CalendarDays} title="No runs today" description="No company has a warrant run scheduled today." testId="overview-today-empty" />}
        </div>
        <div className="space-y-3" data-testid="overview-map-panel">
          <h2 className="font-display text-lg font-bold text-slate-900">Every address today</h2>
          <TeamMap points={points} fitKey={company} className="h-[360px] lg:h-[460px]" testId="overview-map" emptyText={points.length ? null : "No addresses to show today"} />
          <div className="flex flex-wrap gap-3 text-xs text-slate-600" data-testid="overview-map-legend">
            {Object.entries(STATUS_HEX).map(([s, hex]) => <span key={s} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: hex }} />{s.replace("_", " ")}</span>)}
          </div>
        </div>
      </section>
      <section className="space-y-3">
        <h2 className="font-display text-lg font-bold text-slate-900">Next 7 days</h2>
        {upcoming.length ? byDay(upcoming).map(([date, runs]) => (
          <div key={date} className="space-y-2" data-testid={`overview-upcoming-day-${date}`}>
            <p className="font-data text-xs uppercase tracking-wider text-slate-500">{formatDate(date)} · {runs.length} run{runs.length === 1 ? "" : "s"}</p>
            {runs.map((r) => <OverviewRow key={r.id} run={r} />)}
          </div>
        )) : <p className="text-sm text-slate-500" data-testid="overview-upcoming-empty">Nothing scheduled for the coming week.</p>}
      </section>
    </div>
  );
};
